import _ from 'lodash'
import React, { useState, useEffect, useCallback, useRef } from 'react'
import PerfectScrollbar from 'react-perfect-scrollbar'
import { formatDateTime } from 'common/usual'
import {
  Card,
  CardContent,
  CardActions,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Grid,
  TextField,
} from '@material-ui/core'
import { workoutResourceQualityScoreListPromise } from 'api/WorkoutResourceQualityScore'
import { useLocation } from 'react-router-dom'

// The twelve score columns are one per audience segment: non-senior / senior, female / male, and
// BMI low / medium / high. Headers are abbreviated because all twelve have to fit on one row --
// splitting them behind a detail page would hide the only thing worth comparing.
const SCORE_COLUMNS = [
  { key: 'qsNsFL', label: 'NS F/L' },
  { key: 'qsNsFM', label: 'NS F/M' },
  { key: 'qsNsFH', label: 'NS F/H' },
  { key: 'qsNsML', label: 'NS M/L' },
  { key: 'qsNsMM', label: 'NS M/M' },
  { key: 'qsNsMH', label: 'NS M/H' },
  { key: 'qsSrFL', label: 'SR F/L' },
  { key: 'qsSrFM', label: 'SR F/M' },
  { key: 'qsSrFH', label: 'SR F/H' },
  { key: 'qsSrML', label: 'SR M/L' },
  { key: 'qsSrMM', label: 'SR M/M' },
  { key: 'qsSrMH', label: 'SR M/H' },
]

// Read-only. This table is written by the qualityScoreUpdateAutomationV2 cron, which recomputes and
// upserts the whole set weekly, so an edit here would be gone on the next run. To promote a workout
// or a trainer by hand, use the quality score boost page -- those values are applied on top of
// these at request time.
//
// The list is ordered by Updated At because that is what it is for: the cron dying quietly is the
// failure mode on this table, and a top row that is weeks old is the only place it shows.
export default function WorkoutResourceQualityScoreList (props) {
  const ref = useRef()
  const location = useLocation()
  const [data, setData] = useState({})
  const [params, setParams] = useState({})

  // The URL is the source of truth, re-synced on every change, so browser back/forward moves the
  // pagination and the search box and not just the rows.
  useEffect(() => {
    let cancelled = false
    const query = Object.fromEntries(new URLSearchParams(location.search).entries())

    setParams({ page: 1, ...query })
    workoutResourceQualityScoreListPromise(query).then(resp => { if (!cancelled) setData(resp || {}) })

    // Two quick page clicks leave two requests in flight; without this the slower one can land last
    // and repaint the table with the page the admin already left.
    return () => { cancelled = true }
  }, [location.search])

  const handleParamsChange = useCallback((payload, delay = 0) => {
    if (ref.current) clearTimeout(ref.current)

    const query = Object.fromEntries(new URLSearchParams(location.search))
    const next = _.pickBy({ ...query, page: 1, ...payload }, _.identity)
    setParams(next)

    ref.current = setTimeout(() => props.history.push(`?${new URLSearchParams(next)}`), delay)
  }, [location.search, props.history])

  function handlePageChange (event, page) {
    handleParamsChange({ page: page + 1 })
  }

  return (
    <div className={'root'}>
      <div className={'content'}>
        <Grid container item xs={12} spacing={2}>
          <Grid item xs={4}>
            <TextField
              fullWidth
              variant='outlined'
              size='small'
              placeholder='Workout resource id or name'
              value={params.keyword || ''}
              className='background-color'
              onChange={event => handleParamsChange({ keyword: event.target.value }, 500)}
            />
          </Grid>
        </Grid>

        <br />

        <Card>
          <CardHeader
            title='Workout resource quality scores'
            subheader={'Read-only — recomputed weekly by the qualityScoreUpdateAutomationV2 cron, so any edit would be overwritten on the next run. ' +
              'To promote a workout or trainer by hand, use Quality Score Boost. Only resources the cron has scored appear here; ' +
              'it skips resources below their volume and rating thresholds, so a missing workout is normal. ' +
              'If Updated At is old across the whole list, the cron has stopped.'}
          />
          <CardContent className={'table-content'}>
            <PerfectScrollbar>
              <div className={'inner'}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Resource ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Active</TableCell>
                      {SCORE_COLUMNS.map(column => (
                        <TableCell key={column.key}>{column.label}</TableCell>
                      ))}
                      <TableCell>Updated At</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {data.rows && data.rows.map(resource => (
                      <TableRow key={resource.id} hover>
                        <TableCell>{resource.id}</TableCell>
                        <TableCell>{resource.name}</TableCell>
                        <TableCell>{resource.active ? 'TRUE' : 'FALSE'}</TableCell>
                        {SCORE_COLUMNS.map(column => (
                          <TableCell key={column.key}>{(resource.fullQualityScore || {})[column.key]}</TableCell>
                        ))}
                        <TableCell>{formatDateTime((resource.fullQualityScore || {}).updatedAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </PerfectScrollbar>
          </CardContent>

          <CardActions className='flex-right'>
            <TablePagination
              component='div'
              rowsPerPage={10}
              page={(params.page || 1) - 1}
              count={data.count || 0}
              rowsPerPageOptions={[10]}
              onPageChange={handlePageChange}
            />
          </CardActions>
        </Card>
      </div>
    </div>
  )
}
